import RevstackAIInsights from "@/pages/revstack-dashboards/RevstackAIInsights";
import { WidgetCard } from "@components/widgets";
import { ReactComponent as AiIcon } from "@images/ai_icon.svg";
import { each } from "underscore";
import { capitalizeWords } from "@ts/utils";
import { ALL_OPTIONS, DATE_PICKER_PRESET_PERIODS } from "@ts/constants";
import { CA_DD_MODULES, RevstackCASectionIds, RevstackDashboardSubModules, RevstackFrequencySlicerOptions } from "@ts/common-types";
import { getRevstackDDDefaultWidgetConfig, getRevstackDDTableConfig } from "@/ts/revstack/builders";

interface IUseGetRevstackDDTabsConfig {
    drilldownConfig: any[];
    activeTab: RevstackDashboardSubModules | CA_DD_MODULES;
    filterState: any;
    filterDispatch: (action: any) => void;
    widgetData: any;
    drilldownFilters: any;
    layoutType: string;
    selectedTimePeriod?: DATE_PICKER_PRESET_PERIODS;
    granularity?: RevstackFrequencySlicerOptions;
    handlePeriodAndGranularityChange?: (timePeriod: DATE_PICKER_PRESET_PERIODS, granularity: RevstackFrequencySlicerOptions) => void;
    pillFilterDropDownData?: Record<string, any[]>;
    setPillFilterDropDownData?: (cache: Record<string, any[]> | ((prev: Record<string, any[]>) => Record<string, any[]>)) => void;
}


const getSectionIdsByFilter = (
    drilldownConfig: any[],
    filterFn: (widgetConfig: any) => boolean
): RevstackCASectionIds[] => {
    return (drilldownConfig || [])
        .filter(filterFn)
        .map(
            (widgetConfig: any) =>
                widgetConfig.sectionId as RevstackCASectionIds
        );
};

const useGetRevstackDDTabsConfig = ({
    drilldownConfig,
    activeTab,
    filterState,
    filterDispatch,
    drilldownFilters,
    widgetData,
    layoutType,
    selectedTimePeriod,
    granularity,
    handlePeriodAndGranularityChange,
    pillFilterDropDownData,
    setPillFilterDropDownData,
}: IUseGetRevstackDDTabsConfig) => {

    const isAllSelectedInBaseFilter = drilldownFilters?.selected?.subcategory?.includes(ALL_OPTIONS.value) || false;
    
    const overviewWidgetConfig = getRevstackDDDefaultWidgetConfig({
        drilldownConfig: drilldownConfig || [],
        widgetData,
        sectionIds: getSectionIdsByFilter(
            drilldownConfig,
            (widgetConfig) =>
                widgetConfig.moduleType === "default"
        ),
        type: "default",
        handlePeriodAndGranularityChange,
        selectedTimePeriod,
        granularity,
        activeTab,
        pillFilterDropDownData,
        setPillFilterDropDownData,
    });
    
    const tableWidgetConfig = getRevstackDDTableConfig({
        isAllSelectedInBaseFilter,
        drilldownConfig: drilldownConfig || [],
        activeTab,
        filterState,
        filterDispatch,
        widgetData,
        sectionId: getSectionIdsByFilter(
            drilldownConfig,
            (widgetConfig) =>
                widgetConfig.moduleType === "table"
        ),
    });

    const trendsWidgetConfig = getRevstackDDDefaultWidgetConfig({
        drilldownConfig: drilldownConfig || [],
        widgetData,
        sectionIds: getSectionIdsByFilter(
            drilldownConfig,
            (widgetConfig) =>
                widgetConfig.moduleType === "trends"
        ),
        type: "trends",
        selectedTimePeriod,
        granularity,
        handlePeriodAndGranularityChange,
        activeTab,
    });

    const tabConfig = {} as any;
    each(drilldownConfig, (widgetConfig: any) => {
        tabConfig[widgetConfig.tabKey] = {
            sectionId: widgetConfig?.sectionId,
            trendsSectionId: widgetConfig?.trendsSectionId,
            ...(widgetConfig?.config || {}),
        };
    });

    const getWidgetConfig = (module: CA_DD_MODULES, moduleType: any) => {
        const config = tabConfig[module];
        if (!config) return null;
        
        const sectionId = config.sectionId as RevstackCASectionIds;

        if (moduleType === "default") {
            return overviewWidgetConfig[sectionId];
        }

        if (moduleType === "table") {
            return tableWidgetConfig[sectionId];
        }

        if (moduleType === "trends") {
            return trendsWidgetConfig[sectionId];
        }

        return null;
    };
    const tabConfigurations = {} as Record<CA_DD_MODULES, any>;

    // Generate configurations for modules present in drilldownConfig
    (drilldownConfig || []).forEach((widgetConfig: any) => {
        const module = widgetConfig.tabKey as CA_DD_MODULES;
        const moduleType = widgetConfig.moduleType;
        const config = tabConfig[module];

        if (!config) return;

        const widgetCard = widgetConfig.config?.widgetCard || {};
        const defaultProps = {
            className: "h-100 w-100",
            bodyPaddingClass: widgetCard.isBodyPaddingReq ? "px-2" : "p-0",
        };
        
        const children = (
            <WidgetCard {...defaultProps} {...widgetCard}>
                <div
                    key={`drilldown-subcategory-${config.sectionId}`}
                    id={`drilldown-subcategory-${config.sectionId}`}
                >
                    <div
                        key={`drilldown-subcategory-${config.sectionId}`}
                        id={`drilldown-subcategory-${config.sectionId}`}
                    >
                        {getWidgetConfig(module, moduleType)?.component}
                    </div>
                </div>
            </WidgetCard>
        );

        tabConfigurations[module] = {
            key: module,
            label: widgetConfig.title || capitalizeWords(module),
            disabled: widgetConfig.disabled || false,
            children,
        };
    });

    // Always add AI Insights as a special case (not in drilldownConfig)
    tabConfigurations[CA_DD_MODULES.AI_INSIGHTS] = {
        key: CA_DD_MODULES.AI_INSIGHTS,
        label: (
            <div
                className="d-flex align-items-baseline ai-insights-tab"
                style={{ minWidth: "fit-content", whiteSpace: "nowrap" }}
            >
                <AiIcon
                    style={{
                        height: "14px",
                        width: "14px",
                        flexShrink: 0,
                        marginTop: "2px",
                    }}
                />
                <span className="ai-tab-text">AI Insights</span>
            </div>
        ),
        disabled: false,
        children: (
            <div className="ai-insights-content w-100">
                <RevstackAIInsights
                    layoutType={layoutType}
                />
            </div>
        ),
    };

    const configTabs = (drilldownConfig || [])
        .map((widgetConfig: any) => widgetConfig.tabKey as CA_DD_MODULES)
        .filter((tabKey: CA_DD_MODULES, index: number, array: CA_DD_MODULES[]) => 
            // Remove duplicates while preserving order
            array.indexOf(tabKey) === index
        )
        .map((tabKey: CA_DD_MODULES) => tabConfigurations[tabKey])
        .filter(Boolean);

    // Always add AI Insights at the end
    const aiInsightsTab = tabConfigurations[CA_DD_MODULES.AI_INSIGHTS];
    if (aiInsightsTab) {
        configTabs.push(aiInsightsTab);
    }

    const tabItems = configTabs;
    return tabItems;
};

export default useGetRevstackDDTabsConfig;